import { LayoutGrid, List } from 'lucide-react';
import type { RecipesViewPreference } from '../utils/viewPreference';

export interface ViewToggleProps {
  view: RecipesViewPreference;
  onChange: (view: RecipesViewPreference) => void;
}

/** Grid ⊞ / list ☰ toggle for the recipe box (owner ask, 2026-08-04). Two 44px icon buttons
 * in a pill, matching the app's standard icon-button hit target (`h-11 w-11`, e.g.
 * `ListDetailPage`'s header icons) rather than the tag chips' shorter `h-9` — this is a
 * primary control, not a scannable row of options. */
export function ViewToggle({ view, onChange }: ViewToggleProps): JSX.Element {
  return (
    <div
      className="flex shrink-0 items-center gap-0.5 rounded-full border border-line p-0.5"
      data-testid="recipe-view-toggle"
      role="group"
      aria-label="Recipe box view"
    >
      <button
        type="button"
        data-testid="recipe-view-toggle-grid"
        onClick={() => onChange('grid')}
        aria-pressed={view === 'grid'}
        aria-label="Grid view"
        className={`flex h-11 w-11 items-center justify-center rounded-full ease-app ${
          view === 'grid' ? 'bg-inverse text-inverse-fg' : 'text-fg-2'
        }`}
      >
        <LayoutGrid className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>
      <button
        type="button"
        data-testid="recipe-view-toggle-list"
        onClick={() => onChange('list')}
        aria-pressed={view === 'list'}
        aria-label="List view"
        className={`flex h-11 w-11 items-center justify-center rounded-full ease-app ${
          view === 'list' ? 'bg-inverse text-inverse-fg' : 'text-fg-2'
        }`}
      >
        <List className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>
    </div>
  );
}
